import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Heading,
    VStack,
    useToast,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorMode,
    Button,
    Text,
    Spinner,
    Select,
    Badge,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useDisclosure,
    Code,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
    unpaid: 'orange',
    paid: 'green',
    overdue: 'red',
    suspended: 'red',
    disputed: 'purple',
    refunded: 'gray',
    cancelled: 'gray',
};

const InvoicesPage = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [payingId, setPayingId] = useState(null);
    const [paynowInvoice, setPaynowInvoice] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true);
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const response = await axios.get(`/api/protected/invoices${params}`);
            if (response.data.success) {
                setInvoices(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch invoices.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, toast]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    // Check for payment success redirect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('paid') === 'true') {
            toast({
                title: 'Payment Successful',
                description: `Thank you! Invoice ${params.get('invoice') || ''} has been paid.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            // Clean URL
            window.history.replaceState({}, '', '/invoices');
            fetchInvoices();
        }
    }, []);

    const handlePayStripe = async (invoice) => {
        try {
            setPayingId(invoice._id);
            const response = await axios.post(`/api/protected/pay/${invoice._id}`);
            if (response.data.success && response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Error creating payment session:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to create payment session.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setPayingId(null);
        }
    };

    const handlePayNow = (invoice) => {
        setPaynowInvoice(invoice);
        onOpen();
    };

    const handleDownloadPdf = async (invoice) => {
        try {
            const response = await axios.get(`/api/protected/invoices/${invoice._id}/download`, {
                responseType: 'blob', // Important for receiving binary data
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoice.invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast({
                title: 'Download Failed',
                description: 'Could not generate the invoice PDF.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-SG', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const formatPeriods = (periods) => {
        if (!periods || periods.length === 0) return '—';
        return periods.map(p => `${MONTH_NAMES[p.month]} ${p.year}`).join(', ');
    };

    const formatPaymentMethod = (method) => {
        if (!method) return '—';
        const labels = {
            bank_transfer: 'Bank Transfer',
            paynow: 'PayNow',
            stripe: 'Card (Stripe)',
            credit_card: 'Credit Card',
            cash: 'Cash',
            cheque: 'Cheque',
            other: 'Other',
        };
        return labels[method] || method;
    };

    const totalUnpaid = invoices
        .filter(i => ['unpaid', 'overdue', 'suspended'].includes(i.status))
        .reduce((sum, i) => sum + i.totalAmount, 0);

    const isPayable = (status) => ['unpaid', 'overdue'].includes(status);

    return (
        <>
            <Helmet>
                <title>Invoices | AI Call System</title>
                <meta name="description" content="View your invoices and payment history" />
            </Helmet>

            <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
                <Navigation />
                <Box flex="1" p={6} maxW="1200px" mx="auto">
                    <VStack spacing={6} align="stretch">
                        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                            <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                Invoices
                            </Heading>
                            <Flex gap={3} align="center">
                                <Select
                                    size="sm"
                                    w="150px"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    bg={colorMode === 'light' ? 'white' : 'gray.700'}
                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                    borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="unpaid">Unpaid</option>
                                    <option value="paid">Paid</option>
                                    <option value="overdue">Overdue</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="cancelled">Cancelled</option>
                                </Select>
                                <Button
                                    size="sm"
                                    onClick={fetchInvoices}
                                    isLoading={loading}
                                    colorScheme="blue"
                                    variant="outline"
                                >
                                    Refresh
                                </Button>
                            </Flex>
                        </Flex>

                        {totalUnpaid > 0 && (
                            <Box
                                p={4}
                                bg={colorMode === 'light' ? 'orange.50' : 'orange.900'}
                                borderRadius="md"
                                borderWidth={1}
                                borderColor={colorMode === 'light' ? 'orange.200' : 'orange.700'}
                            >
                                <Text fontWeight="semibold" color={colorMode === 'light' ? 'orange.800' : 'orange.200'}>
                                    Outstanding Balance: ${totalUnpaid.toFixed(2)}
                                </Text>
                            </Box>
                        )}

                        {loading ? (
                            <Flex justify="center" py={10}>
                                <Spinner size="xl" />
                            </Flex>
                        ) : invoices.length === 0 ? (
                            <Box
                                p={8}
                                textAlign="center"
                                bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
                                borderRadius="md"
                            >
                                <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                    No invoices found.
                                </Text>
                            </Box>
                        ) : (
                            <Box
                                borderWidth={1}
                                borderRadius="md"
                                borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                                overflowX="auto"
                            >
                                <Table variant="simple" size="sm">
                                    <Thead bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
                                        <Tr>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Invoice #</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Period</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'} isNumeric>Amount ($)</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Status</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Due Date</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Paid</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Payment</Th>
                                            <Th color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>Action</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {invoices.map((inv) => (
                                            <Tr key={inv._id}>
                                                <Td>
                                                    <Text fontWeight="medium" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                        {inv.invoiceNumber}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                        {formatPeriods(inv.periods)}
                                                    </Text>
                                                </Td>
                                                <Td isNumeric fontWeight="semibold" fontSize="sm" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                                    {inv.totalAmount?.toFixed(2)}
                                                </Td>
                                                <Td>
                                                    <Badge
                                                        colorScheme={STATUS_COLORS[inv.status] || 'gray'}
                                                        variant="subtle"
                                                    >
                                                        {inv.status}
                                                    </Badge>
                                                </Td>
                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                    {formatDate(inv.dueDate)}
                                                </Td>
                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                    {formatDate(inv.paidAt)}
                                                </Td>
                                                <Td fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                                                    {formatPaymentMethod(inv.paymentMethod)}
                                                    {inv.transactionNumber && (
                                                        <Text fontSize="xs" color={colorMode === 'light' ? 'gray.400' : 'gray.500'}>
                                                            {inv.transactionNumber}
                                                        </Text>
                                                    )}
                                                </Td>
                                                <Td>
                                                    {isPayable(inv.status) && (
                                                        <Flex gap={2} direction="column">
                                                            <Button
                                                                size="xs"
                                                                colorScheme="blue"
                                                                isLoading={payingId === inv._id}
                                                                onClick={() => handlePayStripe(inv)}
                                                            >
                                                                Pay by Card
                                                            </Button>
                                                            <Button
                                                                size="xs"
                                                                colorScheme="purple"
                                                                variant="outline"
                                                                onClick={() => handlePayNow(inv)}
                                                            >
                                                                PayNow
                                                            </Button>
                                                        </Flex>
                                                    )}
                                                    {!isPayable(inv.status) && (
                                                        <Button
                                                            size="xs"
                                                            leftIcon={<DownloadIcon />}
                                                            onClick={() => handleDownloadPdf(inv)}
                                                            variant="outline"
                                                        >
                                                            Download PDF
                                                        </Button>
                                                    )}
                                                    {isPayable(inv.status) && (
                                                        <Button
                                                            mt={2}
                                                            size="xs"
                                                            leftIcon={<DownloadIcon />}
                                                            onClick={() => handleDownloadPdf(inv)}
                                                            variant="ghost"
                                                        >
                                                            Download PDF
                                                        </Button>
                                                    )}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        )}
                    </VStack>
                </Box>
            </Flex>

            {/* PayNow Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="md">
                <ModalOverlay />
                <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                    <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
                        Pay via PayNow
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {paynowInvoice && (
                            <VStack spacing={4} align="stretch">
                                <Box
                                    p={4}
                                    bg={colorMode === 'light' ? 'purple.50' : 'purple.900'}
                                    borderRadius="md"
                                    borderWidth={1}
                                    borderColor={colorMode === 'light' ? 'purple.200' : 'purple.700'}
                                >
                                    <Text fontSize="sm" color={colorMode === 'light' ? 'purple.800' : 'purple.200'}>
                                        <strong>Invoice:</strong> {paynowInvoice.invoiceNumber}
                                    </Text>
                                    <Text fontSize="sm" color={colorMode === 'light' ? 'purple.800' : 'purple.200'}>
                                        <strong>Amount:</strong> S${paynowInvoice.totalAmount?.toFixed(2)}
                                    </Text>
                                </Box>

                                <Box>
                                    <Text fontWeight="bold" mb={2} color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                        Transfer Details:
                                    </Text>
                                    <Table size="sm" variant="simple">
                                        <Tbody>
                                            <Tr>
                                                <Td fontWeight="medium" fontSize="sm">PayNow UEN</Td>
                                                <Td fontSize="sm"><Code>{process.env.REACT_APP_PAYNOW_UEN || 'Contact admin'}</Code></Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="medium" fontSize="sm">Amount</Td>
                                                <Td fontSize="sm">S${paynowInvoice.totalAmount?.toFixed(2)}</Td>
                                            </Tr>
                                            <Tr>
                                                <Td fontWeight="medium" fontSize="sm">Reference</Td>
                                                <Td fontSize="sm"><Code>{paynowInvoice.invoiceNumber}</Code></Td>
                                            </Tr>
                                        </Tbody>
                                    </Table>
                                </Box>

                                <Box
                                    p={3}
                                    bg={colorMode === 'light' ? 'yellow.50' : 'yellow.900'}
                                    borderRadius="md"
                                    borderWidth={1}
                                    borderColor={colorMode === 'light' ? 'yellow.200' : 'yellow.700'}
                                >
                                    <Text fontSize="xs" color={colorMode === 'light' ? 'yellow.800' : 'yellow.200'}>
                                        Please include the invoice number as payment reference. Your payment will be verified and the invoice will be marked as paid within 1-2 business days.
                                    </Text>
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default InvoicesPage;
